import { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import {
  Box,
  CircularProgress,
  Grid2 as Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  InvoicePaymentCallbackMessage,
  PaymentMethod,
  PaymentSource,
} from "../../enums";
import {
  InvoiceOverviewInfo,
  InvoicePaymentInitiatePayload,
  PaymentDetails,
} from "./../../types";
import {
  CreditCardSale,
  CashPayment,
  DetailsForm,
  HostedPaymentsPage,
  CheckPayment,
  InvoiceSummaryPanel,
} from "./fragments";
import { useValidateForm } from "../../hooks";
import { getPaymentDetailsSchema } from "../../validations";
import { AchSaleWithToken } from "./fragments/AchSaleWithToken";

type PaymentDialogProps = {
  invoiceOverview?: InvoiceOverviewInfo;
  open: boolean;
  handleClose: (message: InvoicePaymentCallbackMessage) => void;
  loading?: boolean;
};

export const PaymentDialog = ({
  invoiceOverview,
  open,
  handleClose,
  loading,
}: PaymentDialogProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isCanada = invoiceOverview?.address.country === "CA";

  const [paymentMethod, setPaymentMethod] = useState(PaymentMethod.Card);
  const [paymentSource, setPaymentSource] = useState<PaymentSource | undefined>(
    undefined,
  );

  const [callbackMessage, setCallbackMessage] = useState(
    InvoicePaymentCallbackMessage.None,
  );

  const [activeStep, setActiveStep] = useState(1);

  const [invoicePaymentPayload, setInvoicePaymentPayload] = useState<
    InvoicePaymentInitiatePayload | undefined
  >(invoiceOverview);

  const schema = useMemo(() => {
    return getPaymentDetailsSchema(
      isCanada,
      invoiceOverview?.balanceAmount,
      paymentMethod,
      paymentSource,
    );
  }, [invoiceOverview, paymentMethod, paymentSource, isCanada]);

  const { register, handleSubmit, errors, reset, setValue } =
    useValidateForm(schema);

  const [amountValue, setAmountValue] = useState<number | undefined>(
    invoiceOverview?.balanceAmount,
  );

  useEffect(() => {
    setInvoicePaymentPayload(invoiceOverview);
  }, [invoiceOverview]);

  useEffect(() => {
    if (open && invoiceOverview) {
      setAmountValue(invoiceOverview.balanceAmount);
      setValue("amount", invoiceOverview.balanceAmount);
    }
  }, [open, invoiceOverview, setValue]);

  useEffect(() => {
    if (paymentMethod === PaymentMethod.Ach && invoiceOverview) {
      setAmountValue(invoiceOverview.balanceAmount);
      setValue("amount", invoiceOverview.balanceAmount);
    }
  }, [paymentMethod, invoiceOverview, setValue]);

  useEffect(() => {
    const handleMessage = (
      event: MessageEvent<{ status?: InvoicePaymentCallbackMessage }>,
    ) => {
      if (event.data?.status === InvoicePaymentCallbackMessage.PaymentSuccess) {
        setCallbackMessage(InvoicePaymentCallbackMessage.PaymentSuccess);
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const handleProceed = (data: PaymentDetails) => {
    if (invoiceOverview) {
      setInvoicePaymentPayload({
        ...invoiceOverview,
        address: {
          addressLine1: data.addressLine1 || "",
          addressLine2: data.addressLine2,
          city: data.city || "",
          state: data.state || "",
          country: data.country || "",
          zip: data.zip || "",
        },
        bankName: data.bankName,
        checkNumber: data.checkNumber,
        invoiceAmount: data.amount || 0,
        paymentMethod: data.paymentMethod,
        omnitokenId: data.omnitokenId,
        tokenId: data.tokenId,
        netTransactionId: data.netTransactionId,
        expirationMonth: data.expirationMonth,
        expirationYear: data.expirationYear,
        saveCard: data.saveCard,
        cardLogo: data.cardLogo,
        tokenType: data.tokenType,
      });
    }

    setActiveStep(2);
  };

  const handleCloseWrapper = () => {
    setActiveStep(1);
    reset();
    setAmountValue(undefined);
    handleClose(callbackMessage);
    setCallbackMessage(InvoicePaymentCallbackMessage.None);
    setPaymentMethod(PaymentMethod.Card);
    setPaymentSource(undefined);
  };

  const renderStepTwo = () => {
    if (!invoicePaymentPayload) return null;

    const hasSavedToken = !!invoicePaymentPayload.omnitokenId;

    switch (invoicePaymentPayload.paymentMethod) {
      case PaymentMethod.Card:
        return hasSavedToken ? (
          <CreditCardSale
            payload={invoicePaymentPayload}
            orgId={invoiceOverview!.orgId}
          />
        ) : (
          <HostedPaymentsPage
            payload={invoicePaymentPayload}
            orgId={invoiceOverview!.orgId}
          />
        );

      case PaymentMethod.Ach:
        return hasSavedToken ? (
          <AchSaleWithToken
            payload={invoicePaymentPayload}
            orgId={invoiceOverview!.orgId}
          />
        ) : (
          <HostedPaymentsPage
            payload={invoicePaymentPayload}
            orgId={invoiceOverview!.orgId}
          />
        );

      case PaymentMethod.Check:
        return (
          <CheckPayment
            payload={invoicePaymentPayload}
            orgId={invoiceOverview!.orgId}
          />
        );

      case PaymentMethod.Cash:
        return (
          <CashPayment
            payload={invoicePaymentPayload}
            orgId={invoiceOverview!.orgId}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseWrapper}
      fullWidth
      fullScreen={isMobile}
      maxWidth={false}
      sx={{
        "& .MuiDialog-paper": {
          width: "100%",
          maxWidth: { xs: "100%", sm: 940 },
          height: {
            xs: "100%",
            sm: "720px",
          },
          maxHeight: {
            xs: "100%",
            sm: "90vh",
          },
          borderRadius: { xs: 0, sm: "12px" },
          overflow: "hidden",
          m: { xs: 0, sm: 2 },
        },
      }}
    >
      {loading || !invoiceOverview ? (
        <Grid
          container
          size={12}
          sx={{
            width: "100%",
            height: "100%",
            minHeight: "75vh",
            direction: "row",
            borderRadius: "10px",
            backgroundColor: "white",
            boxShadow: "0px 0px 4px 0px #00000040",
            p: 3,
            justifyContent: loading ? "center" : "flex-start",
            position: "relative",
            display: "flex",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Grid>
      ) : (
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            width: "100%",
            height: { xs: "auto", md: "100%" },
            minHeight: { xs: "auto", md: 0 },
            overflowY: { xs: "auto", md: "hidden" },
            overflowX: "hidden",
          }}
        >
          <InvoiceSummaryPanel
            invoice={invoiceOverview}
            amountValue={amountValue}
            error={errors["amount"]}
            onAmountChange={(value: number | undefined) => {
              setAmountValue(value);
              setValue("amount", value ?? 0, { shouldValidate: true });
            }}
            onClose={handleCloseWrapper}
            disableAmount={paymentMethod === PaymentMethod.Ach}
          />

          <Box
            sx={{
              width: "100%",
              height: { xs: "auto", md: "100%" },
              minWidth: 0,
              minHeight: { xs: "auto", md: 0 },
              boxSizing: "border-box",
              bgcolor: "#fff",
              display: "flex",
              flexDirection: "column",
              overflow: { xs: "visible", md: "hidden" },
              p: { xs: "16px 16px 0", sm: "20px 20px 0" },
            }}
          >
            <DialogContent
              sx={{
                p: 0,
                m: 0,
                flex: { xs: "none", md: 1 },
                minWidth: 0,
                minHeight: { xs: "auto", md: 0 },
                width: "100%",
                height: { xs: "auto", md: "100%" },
                display: "flex",
                flexDirection: "column",
                overflow: { xs: "visible", md: "hidden" },
              }}
            >
              {invoiceOverview.isTransactionInitiated ? (
                <Box sx={{ p: { xs: 2, sm: 4 }, textAlign: "center" }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Transaction Initiated
                  </Typography>

                  <Typography color="text.secondary">
                    An ACH payment is already being processed for this invoice.
                    It can take 4–5 business days to settle.
                  </Typography>
                </Box>
              ) : activeStep === 1 ? (
                <Box
                  component="form"
                  noValidate
                  id="paymentDetailsForm"
                  onSubmit={handleSubmit(handleProceed)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    height: { xs: "auto", md: "100%" },
                    minHeight: { xs: "auto", md: 0 },
                    overflow: { xs: "visible", md: "hidden" },
                  }}
                >
                  <DetailsForm
                    register={register}
                    errors={errors}
                    invoice={invoiceOverview}
                    setValue={setValue}
                    orgId={invoiceOverview.orgId}
                    accountId={invoiceOverview.accountId}
                    paymentMethod={paymentMethod}
                    setPaymentMethod={setPaymentMethod}
                    paymentSource={paymentSource}
                    setPaymentSource={setPaymentSource}
                    amountValue={amountValue}
                    onCancel={handleCloseWrapper}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    minWidth: 0,
                    minHeight: 0,
                    flex: 1,
                    overflow: "hidden",
                  }}
                >
                  {renderStepTwo()}
                </Box>
              )}
            </DialogContent>
          </Box>
        </Box>
      )}
    </Dialog>
  );
};
